import { useCallback, useEffect, useState } from 'react';
import Swal from 'sweetalert2';
import { apiUrl } from '../config/api';
import { readAuth, clearAuth } from './useAuthStorage';

/**
 * Owns the set of wish-listed product codes and the toggle request.
 *
 * The previous implementation flipped the heart colour with
 * `document.querySelector('.' + product_code).style.color`, which
 * (a) threw a DOMException whenever a product code started with a
 * digit, and (b) desynced from React state on the next re-render.
 * The set below is the single source of truth instead.
 */
const useWishlistActions = ({ onLoginRequired } = {}) => {
  const [wishlistCodes, setWishlistCodes] = useState([]);
  const [pendingCode, setPendingCode] = useState(null);

  const fetchWishlist = useCallback(async (signal) => {
    const { token, user_id } = readAuth();
    if (!token || !user_id) {
      setWishlistCodes([]);
      return;
    }

    try {
      const response = await fetch(apiUrl(`/api/whishlist-product-code/${user_id}`), {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        signal,
      });
      if (!response.ok) throw new Error('Network response was not ok');
      const data = await response.json();
      setWishlistCodes(Array.isArray(data.product_code) ? data.product_code : []);
    } catch (error) {
      if (error.name !== 'AbortError') setWishlistCodes([]);
    }
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    fetchWishlist(controller.signal);
    return () => controller.abort();
  }, [fetchWishlist]);

  const toggleWishlist = useCallback(
    async (productCode) => {
      if (!productCode || pendingCode) return;

      const { token, user_id } = readAuth();

      if (!token || !user_id) {
        clearAuth();
        onLoginRequired?.();
        return;
      }

      setPendingCode(productCode);

      try {
        const response = await fetch(apiUrl('/api/add-whishlist'), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ user_id: parseInt(user_id, 10), product_code: productCode }),
        });

        if (!response.ok) throw new Error('Network response was not ok');

        const data = await response.json();

        setWishlistCodes((prev) => {
          if (data.success) return prev.includes(productCode) ? prev : [...prev, productCode];
          if (data.remove) return prev.filter((code) => code !== productCode);
          return prev;
        });
      } catch (error) {
        Swal.fire({
          icon: 'warning',
          confirmButtonColor: '#a17e33',
          title: 'There seems to be an issue updating your wishlist.',
        });
      } finally {
        setPendingCode(null);
      }
    },
    [pendingCode, onLoginRequired]
  );

  const isWishlisted = useCallback(
    (productCode) => wishlistCodes.includes(productCode),
    [wishlistCodes]
  );

  return { wishlistCodes, isWishlisted, toggleWishlist, pendingCode, refreshWishlist: fetchWishlist };
};

export default useWishlistActions;
